import type { InfiniteData } from "@tanstack/react-query";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useApi } from "@/api/hooks/useApi";
import type { ServicePartnerPageDetailsDto, ServicePartnerPagePaginationResultDto } from "@/api/types";
import { useFlashToast } from "@/components/FlashToast/FlashToast";
import { useProjectId } from "@/hooks/Network/useProjectId";
import { QUERY_KEYS } from "@/query-keys";
import { useTranslation } from "@/translations";

const useDeleteService = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  const projectId = useProjectId();
  const { t } = useTranslation();
  const showFlashToast = useFlashToast();

  return useMutation({
    mutationFn: ({ serviceId }: { serviceId: string }) => api.deleteServicepartnersByIdV1(serviceId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.SERVICE_PARTNERS_ALL(projectId) });
      showFlashToast({
        type: "success",
        title: t("model.service.action.delete.notification.success"),
      });
    },
    onError: () => {
      showFlashToast({
        type: "error",
        title: t("model.service.action.delete.notification.error"),
      });
    },
  });
};

const useUpdateServiceViewCount = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  const projectId = useProjectId();

  return useMutation({
    mutationFn: (payload: { serviceId: string }) => api.putServicepartnersUpdateViewCountV1(payload.serviceId),
    onMutate: (payload) => {
      queryClient.setQueryData<InfiniteData<ServicePartnerPagePaginationResultDto>>(
        QUERY_KEYS.SERVICE_PARTNERS_ALL_INFINITE(projectId),

        (currData) => {
          if (!currData) return currData;

          return {
            ...currData,
            pages: [
              ...currData.pages.map((page) => {
                const newItems = page.items.map((item) => {
                  if (item.id === payload.serviceId) {
                    return {
                      ...item,
                      viewCount: item.viewCount ? item.viewCount + 1 : 1,
                    };
                  }

                  return item;
                });

                return {
                  ...page,
                  items: newItems,
                };
              }),
            ],
          };
        },
      );
      queryClient.setQueryData<ServicePartnerPageDetailsDto>(
        QUERY_KEYS.SERVICE_PARTNER(projectId, payload.serviceId),
        (currData) => {
          if (!currData) return currData;

          return {
            ...currData,
            viewCount: currData.viewCount ? currData.viewCount + 1 : 1,
          };
        },
      );
    },
    onSettled: (_, __, payload) => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.SERVICE_PARTNER(projectId, payload.serviceId) });
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.SERVICE_PARTNERS_ALL_INFINITE(projectId) });
    },
  });
};

export const serviceMutations = {
  useDeleteService,
  useUpdateServiceViewCount,
};
